/**
 * Shared utilities for export functionality (PNG and SVG)
 */

/**
 * Calculate bounding box of all shapes in a fractal tree
 * @param {Array} squares - Array of Square objects
 * @param {Array} triangles - Array of Triangle objects
 * @returns {Object} Bounding box {minX, minY, maxX, maxY, width, height}
 */
export function calculateTreeBounds(squares, triangles = []) {
    let minX = Infinity,
        minY = Infinity;
    let maxX = -Infinity,
        maxY = -Infinity;

    // Process squares
    for (const square of squares) {
        const halfSize = square.size / 2;
        minX = Math.min(minX, square.x - halfSize);
        minY = Math.min(minY, square.y - halfSize);
        maxX = Math.max(maxX, square.x + halfSize);
        maxY = Math.max(maxY, square.y + halfSize);
    }

    // Process triangles
    for (const triangle of triangles) {
        if (triangle.vertices) {
            for (const vertex of triangle.vertices) {
                minX = Math.min(minX, vertex.x);
                minY = Math.min(minY, vertex.y);
                maxX = Math.max(maxX, vertex.x);
                maxY = Math.max(maxY, vertex.y);
            }
        }
    }

    // Handle case where no shapes exist
    if (minX === Infinity) {
        return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
    }

    const width = maxX - minX;
    const height = maxY - minY;

    return { minX, minY, maxX, maxY, width, height };
}

/**
 * Calculate dimensions for cropped export with padding
 * @param {Object} bounds - Tree bounds from calculateTreeBounds
 * @param {number} padding - Padding around tree in pixels (default: 50)
 * @param {number} minSize - Minimum dimension size (default: 400)
 * @returns {Object} Crop dimensions {width, height, x, y}
 */
export function calculateCropDimensions(bounds, padding = 50, minSize = 400) {
    const paddedWidth = bounds.width + padding * 2;
    const paddedHeight = bounds.height + padding * 2;

    // Ensure minimum size for very small trees
    const width = Math.max(paddedWidth, minSize);
    const height = Math.max(paddedHeight, minSize);

    // Calculate top-left corner position
    const x = bounds.minX - padding;
    const y = bounds.minY - padding;

    return { width, height, x, y };
}

/**
 * Calculate transformation to fit tree in export dimensions (for SVG use)
 * @param {Object} bounds - Tree bounds
 * @param {number} width - Export width
 * @param {number} height - Export height
 * @param {number} padding - Padding around tree
 * @returns {Object} Transform parameters {scale, offsetX, offsetY}
 */
export function calculateAutoFitTransform(bounds, width, height, padding = 50) {
    const treeWidth = bounds.maxX - bounds.minX;
    const treeHeight = bounds.maxY - bounds.minY;

    const availableWidth = width - padding * 2;
    const availableHeight = height - padding * 2;

    // Calculate scale to fit both dimensions
    const scaleX = availableWidth / treeWidth;
    const scaleY = availableHeight / treeHeight;
    const scale = Math.min(scaleX, scaleY);

    // Calculate offset to center the tree
    const scaledTreeWidth = treeWidth * scale;
    const scaledTreeHeight = treeHeight * scale;

    const offsetX = (width - scaledTreeWidth) / 2 / scale - bounds.minX;
    const offsetY = (height - scaledTreeHeight) / 2 / scale - bounds.minY;

    return { scale, offsetX, offsetY };
}
